import { RootState } from "@/redux/store";
import { useGetBillingCategoryByCommunity, useGetGlMasterDataByBudgetYear, useGetPaymentTermsByCommunity, useGetPropertiesByCommunity, useGetPropertyBlockByCommunityId, useGetPropertyZoneByCommunityId, useGetVendorListByCommunityId, useGetPropertyOccupancyListByCommunity, useGetGlMasterDataDropdown } from "@/services";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  VENDOR_ID,
  PAYMENT_TERM_ID,
  BILLING_CATEGORY,
  ZONE_ID,
  BLOCK_ID,
  PROPERTY_ID,
  PROPERTY_OWNERSHIP_ID,
  GL_MASTER,
  ALT_GL_MASTER,
  BANK_ACC_GL,
  DIST_ZONE,
  DIST_BLOCK,
  DIST_PROPERTY
} from "./consts";

const useApi = ({configFields}: {configFields: any}) => {
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  
  const communityId = communityData?.communityId;

  const activeFields = useMemo(() => {
    const applicable = new Set<string>();
    configFields?.forEach((f: any) => {
      if (f.applicable) {
        applicable.add(f.fieldName?.toLowerCase());
      }
    });
    return applicable;
  }, [configFields]);

  const isEnabled = (key: string) => !!(communityId && activeFields.has(key.toLowerCase()));

  const { data: vendorList } = useGetVendorListByCommunityId({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(VENDOR_ID),
    },
  });

  const { data: paymentTermOptions } = useGetPaymentTermsByCommunity({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(PAYMENT_TERM_ID),
    },
  });

  const { data: billingCategoryList } = useGetBillingCategoryByCommunity({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(BILLING_CATEGORY),
    },
  });

  const { data: zoneList } = useGetPropertyZoneByCommunityId({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(ZONE_ID) || isEnabled(DIST_ZONE),
    },
  });

  const { data: blockList } = useGetPropertyBlockByCommunityId({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(BLOCK_ID) || isEnabled(DIST_BLOCK),
    },
  });

  const { data: propertyList } = useGetPropertiesByCommunity({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(PROPERTY_ID) || isEnabled(DIST_PROPERTY),
    },
  });

  const { data: propertyOccupancyList } = useGetPropertyOccupancyListByCommunity({
    communityId: communityId,
    queryParams: {
      enabled: isEnabled(PROPERTY_OWNERSHIP_ID),
    },
  });

  const { data: glData } = useGetGlMasterDataByBudgetYear({
    communityId: communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: isEnabled(GL_MASTER),
    },
  });

  const { data: glAccount } = useGetGlMasterDataDropdown(
    {
      communityId: communityData?.communityId as string,
      queryParams: {
        enabled: isEnabled(GL_MASTER) || isEnabled(ALT_GL_MASTER) || isEnabled(BANK_ACC_GL),
      },
    }
  );

  return {
    vendorList,
    paymentTermOptions,
    billingCategoryList,
    zoneList,
    blockList,
    propertyList,
    propertyOccupancyList,
    glData: glData?.data,
    glAccount: glAccount?.data
  }
}

export default useApi;